import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Settings2, Route, Tag } from 'lucide-react';
import { ExternalSourcesManager } from '@/components/ExternalSourcesManager';

const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
const selectContentCls = "bg-[#0A1F1A] border-white/10";

export default function CRMSettings() {
  const [customFields, setCustomFields] = useState([]);
  const [adRouting, setAdRouting] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [newField, setNewField] = useState({ name: '', field_type: 'text', options: '' });
  const [newRule, setNewRule] = useState({ ad_name_contains: '', broker_id: '', broker_name: '' });

  useEffect(() => {
    Promise.all([api.get('/crm/custom-fields'), api.get('/crm/ad-routing'), api.get('/users/brokers')])
      .then(([cf, ar, b]) => { setCustomFields(cf); setAdRouting(ar); setBrokers(b); });
  }, []);

  const addField = async () => {
    if (!newField.name.trim()) return toast.error('Nombre requerido');
    const payload = { ...newField, options: newField.field_type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : [] };
    await api.post('/crm/custom-fields', payload);
    setNewField({ name: '', field_type: 'text', options: '' });
    setCustomFields(await api.get('/crm/custom-fields'));
    toast.success('Campo creado');
  };

  const deleteField = async (id) => {
    await api.del(`/crm/custom-fields/${id}`);
    setCustomFields(prev => prev.filter(f => f.id !== id));
    toast.success('Campo eliminado');
  };

  const addRule = async () => {
    if (!newRule.ad_name_contains.trim() || !newRule.broker_id) return toast.error('Palabra clave y broker requeridos');
    await api.post('/crm/ad-routing', newRule);
    setNewRule({ ad_name_contains: '', broker_id: '', broker_name: '' });
    setAdRouting(await api.get('/crm/ad-routing'));
    toast.success('Regla creada');
  };

  const deleteRule = async (id) => {
    await api.del(`/crm/ad-routing/${id}`);
    setAdRouting(prev => prev.filter(r => r.id !== id));
    toast.success('Regla eliminada');
  };

  const FIELD_TYPES = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Numerico' },
    { value: 'checkbox', label: 'Checkbox (Si/No)' },
    { value: 'select', label: 'Lista de opciones' },
  ];

  return (
    <div data-testid="crm-settings-page" className="space-y-6">
      <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white flex items-center gap-3">
        <Settings2 size={24} strokeWidth={1.5} className="text-[#A38560]" /> Configuracion CRM
      </h1>

      {/* Custom Fields */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
        <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2">
          <Tag size={18} className="text-[#A38560]" /> Campos Personalizados para Leads
        </h2>
        <p className="text-sm text-white/40">Agrega campos adicionales que apareceran al crear y editar leads.</p>

        <div className="bg-[#16302B] rounded-xl border border-white/5 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-white/50 text-xs mb-1 block">Nombre del campo</Label>
              <Input value={newField.name} onChange={e => setNewField({ ...newField, name: e.target.value })} placeholder="Ej: Terraza" className={inputCls} data-testid="new-field-name" />
            </div>
            <div>
              <Label className="text-white/50 text-xs mb-1 block">Tipo</Label>
              <Select value={newField.field_type} onValueChange={v => setNewField({ ...newField, field_type: v })}>
                <SelectTrigger className={inputCls} data-testid="new-field-type"><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newField.field_type === 'select' && (
              <div>
                <Label className="text-white/50 text-xs mb-1 block">Opciones (separadas por coma)</Label>
                <Input value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} placeholder="Opcion 1, Opcion 2..." className={inputCls} />
              </div>
            )}
          </div>
          <button onClick={addField} data-testid="add-field-btn"
            className="flex items-center gap-2 bg-[#A38560] text-[#03110D] font-medium px-4 py-2 rounded-xl text-sm hover:bg-[#8B6F47] transition-colors">
            <Plus size={14} /> Agregar Campo
          </button>
        </div>

        <div className="space-y-2">
          {customFields.map(cf => (
            <div key={cf.id} data-testid={`field-${cf.id}`} className="flex items-center justify-between bg-[#16302B] rounded-xl border border-white/5 p-3">
              <div>
                <p className="text-sm text-white font-medium">{cf.name}</p>
                <p className="text-xs text-white/40">{FIELD_TYPES.find(t => t.value === cf.field_type)?.label || cf.field_type}
                  {cf.options?.length > 0 && ` · ${cf.options.join(', ')}`}
                </p>
              </div>
              <button onClick={() => deleteField(cf.id)} className="text-white/20 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
          {customFields.length === 0 && <p className="text-sm text-white/30 text-center py-4">No hay campos personalizados</p>}
        </div>
      </div>

      {/* Ad Routing Rules */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
        <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2">
          <Route size={18} className="text-[#6BCB77]" /> Reglas de Asignacion Automatica (Meta Ads)
        </h2>
        <p className="text-sm text-white/40">Cuando un lead llega de Meta, si el nombre del anuncio contiene la palabra clave, se asigna automaticamente al broker indicado.</p>

        <div className="bg-[#16302B] rounded-xl border border-white/5 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-white/50 text-xs mb-1 block">Palabra clave en el anuncio</Label>
              <Input value={newRule.ad_name_contains} onChange={e => setNewRule({ ...newRule, ad_name_contains: e.target.value })} placeholder="Ej: Rosales, Bosque de pinos..." className={inputCls} data-testid="new-rule-keyword" />
            </div>
            <div>
              <Label className="text-white/50 text-xs mb-1 block">Asignar a broker</Label>
              <Select value={newRule.broker_id} onValueChange={v => { const b = brokers.find(x => x.id === v); setNewRule({ ...newRule, broker_id: v, broker_name: b?.name || '' }); }}>
                <SelectTrigger className={inputCls} data-testid="new-rule-broker"><SelectValue placeholder="Seleccionar broker" /></SelectTrigger>
                <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <button onClick={addRule} data-testid="add-rule-btn"
            className="flex items-center gap-2 bg-[#6BCB77]/20 text-[#6BCB77] font-medium px-4 py-2 rounded-xl text-sm hover:bg-[#6BCB77]/30 transition-colors">
            <Plus size={14} /> Agregar Regla
          </button>
        </div>

        <div className="space-y-2">
          {adRouting.map(rule => (
            <div key={rule.id} data-testid={`rule-${rule.id}`} className="flex items-center justify-between bg-[#16302B] rounded-xl border border-white/5 p-3">
              <div>
                <p className="text-sm text-white">Si el anuncio contiene "<span className="text-[#A38560] font-medium">{rule.ad_name_contains}</span>"</p>
                <p className="text-xs text-white/40">Asignar a <span className="text-[#6BCB77]">{rule.broker_name}</span></p>
              </div>
              <button onClick={() => deleteRule(rule.id)} className="text-white/20 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
          {adRouting.length === 0 && <p className="text-sm text-white/30 text-center py-4">No hay reglas de asignacion</p>}
        </div>
      </div>

      <ExternalSourcesManager />

      {/* Webhook Info */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-3">
        <h2 className="font-outfit text-lg font-medium text-white">Webhook de Meta</h2>
        <p className="text-sm text-white/40">Configura este URL en tu app de Meta for Developers para recibir leads automaticamente:</p>
        <div className="bg-[#16302B] rounded-xl p-3 font-mono text-sm text-[#A38560] break-all select-all">
          {window.location.origin}/api/webhook
        </div>
        <p className="text-xs text-white/30">Verify Token: <span className="text-white/50 font-mono">SELECTO2026</span></p>
      </div>
    </div>
  );
}
